import type { Blueprint, Kitchen } from '@rta/rta-blueprint-library';

import { formatBlueprintDate } from './format-blueprint-date';
import { overrideToPlainText } from '../shared/sanitize-override-html';

/** Where the BP's own creation date lives once stamped — the revision log's first row, so the header and that row are literally the same field. */
export const INITIAL_DATE_OVERRIDE_KEY = 'revision.0.date';

export type GetBlueprintCreationDateParams = {
	kitchen: Kitchen | null;
	blueprint: Blueprint | null;
};

/**
 * The date this blueprint was first made, formatted MM/DD/YY — the one value the page header's
 * Creation Date and the revision log's "Initial BP creation" row both read, so they can't disagree.
 * Once `stampInitialBlueprintDate` has frozen it (at the first PDF/PPTX export) that stamp IS the
 * answer and nothing recomputes it; everything below is how the stamp itself is first derived.
 *
 * Source order:
 *   1. `blueprint.submissionDate` — set once at Blueprint construction (defaults to `Date.now()`,
 *      or carries the HubSpot submission timestamp when the BP came from there) and never cloned
 *      away, so it IS the true original creation date. The earliest kitchen `createDate` would be
 *      wrong whenever the BP sits unsaved for a while: `Kitchen.clone()` deletes `createDate`,
 *      so the earliest *surviving* version's date is actually the day a version was first SAVED,
 *      not the day the BP was created (#607).
 *   2. Earliest `createDate` across `blueprint.kitchens.children` — only used when the blueprint
 *      has no parseable submissionDate; still better than the live kitchen's own `createDate`,
 *      which always dates to the day the current draft was made (#416).
 *   3. The live kitchen's `createDate` — last-ditch fallback for a blueprint with nothing but a
 *      first draft.
 */
export const getBlueprintCreationDate = (params: GetBlueprintCreationDateParams): string => {
	const { kitchen, blueprint } = params;

	const stamped = overrideToPlainText(kitchen?.blueprintOverrides?.[INITIAL_DATE_OVERRIDE_KEY] ?? '').trim();
	if (stamped) return stamped;

	const submission = parseBlueprintSubmissionDate(blueprint?.submissionDate);
	if (submission) return formatBlueprintDate(submission);

	const earliest = (blueprint?.kitchens?.children ?? []).reduce<Date | null>((oldest, version) => {
		const { createDate } = version;
		if (!createDate || isNaN(createDate.getTime())) return oldest;
		return !oldest || createDate < oldest ? createDate : oldest;
	}, null);

	return formatBlueprintDate(earliest ?? kitchen?.createDate);
};

// `submissionDate` is stored as a string — a numeric ms epoch when the Blueprint defaults it (`Date.now().toString()`), an ISO date string when it carries a HubSpot payload.
const parseBlueprintSubmissionDate = (raw: string | null | undefined): Date | null => {
	if (!raw) return null;
	const asNumber = Number(raw);
	const date = Number.isFinite(asNumber) ? new Date(asNumber) : new Date(raw);
	return isNaN(date.getTime()) ? null : date;
};
